/// <amd-module name="ExtensionManagement/ManagementHub/UninstallExtensionDialog" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { Dialog } from "VSSUI/Dialog";
import { TextField } from "VSSUI/TextField";
import { Checkbox } from "VSSUI/Checkbox";
import { IExtensionViewModel } from "./UIContracts";
import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";

interface IUninstallExtensionDialogProps {
    /**
     * Installed extension to be uninstalled
     */
    extension: IExtensionViewModel;

    /**
     * Callback function if users uninstalls the extension from dialog
     */
    onContinueUninstall: (reason?: string, reasonCode?: string) => void;

    /**
     * Callback function on dialog dismiss
     */
    onDismiss: () => void;
}

/**
 * Dialog that lets user uninstall an extension with option to
 * select provided reasons or type new reason
 */
export class UninstallExtensionDialog extends React.Component<IUninstallExtensionDialogProps> {
    otherReasonText = new ObservableValue<string>("");

    reasons = [
        { id: "missingfeatures", text: Resources.UninstallReasonMissingFeatures, selection: new ObservableValue<boolean>(false) },
        { id: "pooruserexperience", text: Resources.UninstallReasonNotUserFriendly, selection: new ObservableValue<boolean>(false) },
        { id: "didnotworkproperly", text: Resources.UninstallReasonBugs, selection: new ObservableValue<boolean>(false) },
        { id: "justtrying", text: Resources.UninstallReasonJustTrying, selection: new ObservableValue<boolean>(false) },
        { id: "tooexpensive", text: Resources.UninstallReasonCost, selection: new ObservableValue<boolean>(false) },
        { id: "developing", text: Resources.UninstallReasonDeveloping, selection: new ObservableValue<boolean>(false) },
        { id: "other", text: Resources.UninstallReasonOther, selection: new ObservableValue<boolean>(false) }
    ];

    public render(): JSX.Element {
        return (
            <Dialog
                titleProps={{ text: format(Resources.UninstallExtension, this.props.extension.extensionName) }}
                footerButtonProps={[
                    { text: Resources.ContinueUninstall, primary: true, onClick: this.onClickUninstall },
                    {
                        text: Resources.Cancel,
                        onClick: this.props.onDismiss
                    }
                ]}
                onDismiss={this.props.onDismiss}
            >
                <div className="flex-grow">
                    <label>{Resources.UninstallReasonRequest}</label>
                    <div className="rhythm-vertical-8 flex-column padding-left-8 margin-vertical-4">
                        { this.reasons.map((reason) => {
                            let checkbox = (
                                    <Checkbox key={reason.id}
                                        checked={reason.selection}
                                        className="uninstall-extension-checkbox-row"
                                        onChange={(event, checked) => reason.selection.value = checked}
                                        label={reason.text}
                                    />         
                            );
                            return checkbox;
                            })   
                        }
                    </div>
                    <TextField
                        className="textfield-uninstall-reason"
                        label={Resources.CommentRequest}
                        value={this.otherReasonText}
                        onChange={this.onChangeOtherReason}
                        multiline={true}
                        rows={5}
                        maxLength={300}
                    />
                </div>
            </Dialog>
        );
    }

    private onChangeOtherReason = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
        this.otherReasonText.value = newValue;
    };

    /** 
     * retrieve all selected reasons for uninstall
     */
    private onClickUninstall = (): void => {
        //concatenate all selected reason's codes
        let selectedReasons: string[] = [];
        this.reasons.forEach(reason => {
            if (reason.selection.value) {
                selectedReasons.push(reason.id);
            }
        });
        const selectedReasonsText = selectedReasons.length ? selectedReasons.join(",") : "None";
        this.props.onContinueUninstall(this.otherReasonText.value, selectedReasonsText);
    };
}
